import logo from "../../assets/wirder-logo.png";

/**
 * Shared, one-shot preload/decode of the Wirder mascot PNG.
 *
 * The connecting screen flies the logo in with a CSS animation. If the image
 * isn't decoded yet when that animation runs, the logo either pops in partway
 * through the fly-in or the whole screen reads as blank until the browser
 * finishes decoding — the exact "white screen then jump to Home" and
 * "logo loads partway through" glitches we hit on device. Kicking off (and
 * awaiting) a decode lets callers hold the animation until the asset is ready.
 *
 * The promise resolves once the image is loaded and decoded, and also resolves
 * (never rejects) on error or in non-DOM environments, so callers can always
 * gate on it without risking a hang.
 */
let logoReadyPromise: Promise<void> | undefined;

export function preloadLogo(): Promise<void> {
  if (!logoReadyPromise) {
    logoReadyPromise = new Promise<void>((resolve) => {
      if (typeof Image === "undefined") {
        resolve();
        return;
      }
      const img = new Image();
      const done = () => resolve();
      img.onload = () => {
        // `decode()` guarantees the bitmap is ready to paint, so the very first
        // animation frame shows the mascot rather than triggering a decode
        // hitch mid-fly-in. Fall back to onload if decode is unavailable/fails.
        if (typeof img.decode === "function") {
          img.decode().then(done, done);
        } else {
          done();
        }
      };
      img.onerror = done;
      img.src = logo;
    });
  }
  return logoReadyPromise;
}

export { logo };
